"use client";

import { useEffect, useState } from "react";
import { BookingCta } from "@/components/dubai/ui/BookingCta";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
import { WhyEntrepreneursPreferIfzaSection } from "@/components/dubai/sections/WhyEntrepreneursPreferIfzaSection/WhyEntrepreneursPreferIfzaSection";

export const EntrepreneurValuePropositionSection = () => {
  const [formationProgress, setFormationProgress] = useState(0);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [cardSequence, setCardSequence] = useState(0);

  useEffect(() => {
    let progressFrame = 0;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const sequenceDelays: ReturnType<typeof setTimeout>[] = [];

    // Both of these are deferred into the same frame. Under reduced motion the
    // card sequence used to be set synchronously here, which React flags as a
    // cascading render (`react-hooks/set-state-in-effect`); folding it into the
    // reveal frame keeps the end state identical -- everything is already
    // visible on the first painted frame -- without the extra render pass.
    const revealFrame = requestAnimationFrame(() => {
      setHeroRevealed(true);
      if (reducedMotion) setCardSequence(8);
    });

    if (!reducedMotion) {
      for (let step = 1; step <= 8; step += 1) {
        sequenceDelays.push(
          setTimeout(() => setCardSequence(step), 300 + step * 380),
        );
      }
    }

    const progressDelay = setTimeout(() => {
      const targetProgress = 72;
      if (reducedMotion) {
        setFormationProgress(targetProgress);
        return;
      }

      const startTime = performance.now();
      const duration = 3000;
      const animateProgress = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const easedProgress = 1 - (1 - progress) ** 3;
        setFormationProgress(Math.round(targetProgress * easedProgress));
        if (progress < 1) progressFrame = requestAnimationFrame(animateProgress);
      };
      progressFrame = requestAnimationFrame(animateProgress);
    }, 1700);

    return () => {
      cancelAnimationFrame(revealFrame);
      cancelAnimationFrame(progressFrame);
      clearTimeout(progressDelay);
      sequenceDelays.forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="relative flex w-full flex-col items-center bg-black">
      <section
        aria-label="IFZA company setup"
        className="relative w-full overflow-hidden bg-black lg:aspect-[8/5] lg:min-h-0 lg:max-h-screen"
      >
        <img
          src="/dubai/hero-bg.svg"
          alt="Dubai business environment"
          className="absolute h-full min-h-[600px] w-full object-cover object-center"
        />
        <div aria-hidden="true" className="absolute inset-0 h-full min-h-[600px] w-full bg-black/40" />
        <div
          aria-hidden="true"
          className={`absolute left-[-50.903vw] top-[-22.29vw] z-20 hidden h-[107.222vw] w-[107.222vw] rounded-full border-[3px] border-solid border-white transition-transform duration-700 ease-out lg:block lg:origin-[107.5%_35%] min-[1600px]:origin-[104.5%_33.3%] min-[1536px]:duration-700 ${heroRevealed ? "scale-[0.045] min-[1536px]:scale-[0.040]" : "scale-100"}`}
        />
        <div
          aria-hidden="true"
          className={`absolute left-[44.722vw] top-[-22.29vw] z-20 hidden h-[107.222vw] w-[107.222vw] rounded-full transition-all duration-700 ease-out lg:block lg:origin-[18.04%_35%] min-[1600px]:origin-[15.04%_33.3%] min-[1536px]:duration-700 ${heroRevealed ? "scale-[0.045] bg-white min-[1536px]:scale-[0.040]" : "scale-100 bg-[linear-gradient(270deg,rgba(0,97,254,1)_0%,rgba(0,97,254,1)_100%)]"}`}
        />

        <div className="relative z-10 mx-auto flex max-w-[1240px] flex-col gap-8 px-6 pt-[calc(76px+4rem)] pb-16 sm:gap-10 sm:px-10 sm:pt-[calc(76px+5rem)] sm:pb-20 lg:absolute lg:inset-0 lg:block lg:gap-0 lg:px-0 lg:py-0">
          <div
            className={`w-full font-suisse-intl text-[clamp(2.5rem,9vw,100px)] font-semibold uppercase leading-[0.95] text-white transition-all duration-700 ease-out lg:absolute lg:left-0 lg:top-[22%] lg:w-[70%] lg:text-[clamp(2.7rem,11vw,100px)] lg:leading-[100px] ${heroRevealed ? "translate-x-0 opacity-100" : "-translate-x-[120%] opacity-0"}`}
          >
            IFZA Free Zone
            <br />
            Company Setup
            <br />
            in Dubai
          </div>

          <div className="flex w-full flex-col items-start gap-6 sm:w-[70%] lg:absolute lg:left-[20.333%] lg:top-[61.667%] lg:w-[50.486%] lg:items-end lg:gap-[29px]">
            <p className={`w-full font-suisse-intl text-base font-medium leading-7 text-white transition-opacity duration-700 sm:text-lg sm:leading-[31.1px] lg:w-[91.05%] ${heroRevealed ? "opacity-100" : "opacity-0"}`}>
              Set up your UAE company with 100% foreign ownership, remote
              incorporation, visa assistance, banking support, and complete
              guidance from application to licence issuance.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:mr-10">
              <BookingCta
                className="relative h-auto overflow-hidden rounded-xl bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] px-3.5 py-2 font-figtree text-base font-semibold tracking-[-0.32px] text-white shadow-[inset_0px_0px_22px_#f2f2f280,inset_0px_0px_0px_1px_#999999,inset_-2px_-2px_1px_-2px_#b3b3b3,inset_2px_2px_1px_-2px_#b3b3b3] backdrop-blur-[6px]"
              >
                <span aria-hidden="true" className="absolute left-[-182px] top-10 h-20 w-[326px] rounded-[163px/40px] bg-[#6FA6FF] blur-[27px]" />
                <span className="relative px-1 leading-7">SEE IF IFZA FITS MY BUSINESS</span>
              </BookingCta>
              <button
                type="button"
                onClick={() => openWhatsAppWithUtm(WA_NUMBERS, "dubai")}
                className="inline-flex cursor-pointer items-center gap-1 rounded-xl px-2.5 py-3 font-suisse-intl text-base font-bold leading-[18px] text-white outline outline-1 outline-[#A1A1A1] transition-opacity hover:opacity-80 active:opacity-60"
              >
                Chat with a Specialist
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 10c0 4.142-3.358 7.5-7.5 7.5a7.47 7.47 0 0 1-3.75-1L2.5 17.5l1-3.75A7.47 7.47 0 0 1 2.5 10C2.5 5.858 5.858 2.5 10 2.5S17.5 5.858 17.5 10Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div
            aria-hidden="true"
            className="relative z-10 flex w-full flex-col items-start gap-2 rounded-3xl border border-[#ffffff1a] bg-[#ffffff0d] p-5 shadow-[0px_1px_0px_1px_#ffffff14_inset,0px_35px_90px_#080b091a] backdrop-blur-xl [-webkit-backdrop-filter:blur(24px)] sm:w-[331px] sm:self-end sm:p-6 lg:absolute lg:bottom-5 lg:left-auto lg:right-6 lg:w-[331px]"
          >
            <div className="flex w-full items-center justify-between">
              <span className="font-suisse-intl text-[14.4px] font-medium uppercase leading-[15.6px] tracking-[1.46px] text-white">MUTETAXES</span>
              <div className="flex items-center gap-[6.39px] rounded-full bg-[#6FA6FF]/10 px-[10.4px] py-[6.72px] [outline:1px_solid_#FFFFFF] [outline-offset:-1px]">
                <div className="h-[6.39px] w-[6.39px] rounded-full bg-white " />
                <span className="font-suisse-intl text-[9.9px] leading-[14.88px] text-white">Application active</span>
              </div>
            </div>
            <div className="flex h-[55px] w-full items-center justify-between">
              <div className="flex flex-col">
                <span className="font-suisse-intl text-[11.7px] leading-[17.52px] text-white">IFZA setup</span>
                <span className="font-suisse-intl text-[20.5px] leading-[30.72px] text-white">Formation progress</span>
              </div>
              <span className="font-suisse-intl text-2xl font-bold leading-6 text-[#1991fe]">{formationProgress}%</span>
            </div>
            <div className="relative h-[7.19px] w-full overflow-hidden rounded-full bg-[#ffffff1f]">
              <div className="absolute left-0 top-0 h-full rounded-full bg-[#1991fe] transition-[width] duration-75 ease-out" style={{ width: `${formationProgress}%` }} />
            </div>
            <div className="flex w-full flex-col items-start pt-[8.79px]">
              <div className="flex w-full items-center gap-[13.59px] rounded-2xl px-3 py-1.5">
                <img src="/dubai/check.svg" alt="" aria-hidden="true" className={`h-6 w-6 shrink-0 transition-[opacity,transform] duration-300 ${cardSequence >= 1 ? "scale-100 opacity-100" : "scale-50 opacity-0"}`} />
                <div className={`transition-[opacity,transform] duration-300 ${cardSequence >= 2 ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
                  <p className="font-suisse-intl text-[12.2px] font-bold leading-[18.24px] text-white/80">Business review</p>
                  <p className="font-suisse-intl text-[10.4px] leading-[15.6px] text-[#a0a4b3]">Completed</p>
                </div>
              </div>
              <div className="flex w-full items-center gap-[13.59px] rounded-2xl px-3 py-1.5">
                <img src="/dubai/check.svg" alt="" aria-hidden="true" className={`h-6 w-6 shrink-0 transition-[opacity,transform] duration-300 ${cardSequence >= 3 ? "scale-100 opacity-100" : "scale-50 opacity-0"}`} />
                <div className={`transition-[opacity,transform] duration-300 ${cardSequence >= 4 ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
                  <p className="font-suisse-intl text-[12.2px] font-bold leading-[18.24px] text-white/80">Documents verified</p>
                  <p className="font-suisse-intl text-[10.4px] leading-[15.6px] text-[#a0a4b3]">Completed</p>
                </div>
              </div>
              <div className={`flex w-full items-center gap-[13.59px] rounded-2xl px-3 py-1.5 transition-colors duration-300 ${cardSequence >= 5 ? "bg-[#6FA6FF]/[0.08] [outline:1px_solid_#6FA6FF29] [outline-offset:-1px]" : ""}`}>
                <div className={`flex h-6 w-6 items-center justify-center rounded-xl [outline:1px_solid_#ffffff2e] transition-[opacity,transform] duration-300 ${cardSequence >= 5 ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}><span className="text-center font-suisse-intl text-[10.4px] text-white">3</span></div>
                <div className={`transition-[opacity,transform] duration-300 ${cardSequence >= 6 ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}><p className="font-suisse-intl text-[12.2px] font-bold leading-[18.24px] text-white">Authority review</p><p className="font-suisse-intl text-[10.4px] leading-[15.6px] text-[#a0a4b3]">In progress</p></div>
              </div>
              <div className="flex w-full items-center gap-[13.59px] rounded-2xl px-3 py-1.5">
                <div className={`flex h-6 w-6 items-center justify-center rounded-xl [outline:1px_solid_#ffffff2e] transition-[opacity,transform] duration-300 ${cardSequence >= 7 ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}><span className="text-center font-suisse-intl text-[10.4px] text-[#a0a4b3]">4</span></div>
                <div className={`transition-[opacity,transform] duration-300 ${cardSequence >= 8 ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}><p className="font-suisse-intl text-[12.2px] font-bold leading-[18.24px] text-[#a0a4b3]">Licence issuance</p><p className="font-suisse-intl text-[10.4px] leading-[15.6px] text-[#a0a4b3]">Up next</p></div>
              </div>
            </div>
            <div className="flex items-center gap-1"><img src="/dubai/guard.svg" alt="" aria-hidden="true" className="h-6 w-4 shrink-0" /><span className="w-[182.14px] font-suisse-intl text-[10.9px] leading-[16.32px] text-white">Managed from application to licence</span></div>
          </div>
        </div>
      </section>
      <WhyEntrepreneursPreferIfzaSection />
    </div>
  );
};
